import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  DollarSign,
  Globe,
  MousePointerClick,
  Search,
  Send,
  Sparkles,
  Target,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { DemoModal } from "@/components/DemoModal";
import { Logo } from "@/components/Logo";
import { useDiscoveryHost } from "@/components/discovery-host-context";
import { brand } from "@/lib/brand";
import { resolveCurrentPublicDiscoverySlug } from "@/lib/customer-resolution";
import { useBusinessOnboardingHost } from "@/business-onboarding/onboarding-host-context";
import { useManagedCompanies } from "@/auth/useManagedCompanies";

const landingTitle = "Estimate Widget for Contractors | Qotta";
const landingDescription =
  "Turn more website visitors into qualified leads with a smarter estimate widget that guides buyers to the right solution and personalized price range.";
const landingUrl = "https://qotta.co/estimate-widget-for-contractors";

const landingStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://qotta.co/estimate-widget-for-contractors#webpage",
  name: landingTitle,
  url: landingUrl,
  description: landingDescription,
  publisher: {
    "@id": "https://qotta.co/#organization",
  },
} as const;

export const Route = createFileRoute("/estimate-widget-for-contractors")({
  validateSearch: (search: Record<string, unknown>): { view?: "public" } => search["view"] === "public" ? { view: "public" } : {},
  head: () => ({
    meta: [
      { title: landingTitle },
      { name: "description", content: landingDescription },
      { property: "og:title", content: landingTitle },
      { property: "og:description", content: landingDescription },
      { property: "og:url", content: landingUrl },
      { property: "og:image", content: brand.socialImageUrl },
      { property: "og:image:alt", content: brand.socialImageAlt },
      { name: "twitter:title", content: landingTitle },
      { name: "twitter:description", content: landingDescription },
      { name: "twitter:image", content: brand.socialImageUrl },
      { name: "twitter:image:alt", content: brand.socialImageAlt },
    ],
    links: [{ rel: "canonical", href: landingUrl }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(landingStructuredData),
      },
    ],
  }),
  component: EstimateWidgetForContractors,
});

const steps = [
  {
    title: "Understand the project",
    body: "Qotta asks focused questions based on your actual services, products, pricing logic, and business knowledge.",
    icon: Search,
  },
  {
    title: "Guide the visitor to the right solution",
    body: "Instead of showing every possible option, Qotta narrows the path based on what the visitor actually needs.",
    icon: Target,
  },
  {
    title: "Provide a personalized estimate",
    body: "Once enough information is available, the visitor receives a useful price range based on the relevant solution and project context.",
    icon: DollarSign,
  },
  {
    title: "Deliver a qualified lead",
    body: "Your team receives the contact details together with the discovery context: what the customer needs, what they selected, and the estimate they saw.",
    icon: Send,
  },
] as const;

const projectBusinesses = [
  "Kitchen remodeling",
  "Bathroom remodeling",
  "Roofing",
  "HVAC",
  "Windows and doors",
  "Pergolas and outdoor structures",
  "Flooring",
  "Landscaping",
  "Custom home improvement services",
] as const;

function EstimateWidgetForContractors() {
  const publicView = Route.useSearch().view === "public";
  const { companies } = useManagedCompanies();
  const { openOnboarding, canOpenOnboarding } = useBusinessOnboardingHost();
  const isExistingManagerPublicView = publicView && Boolean(companies?.length);
  const { openDiscovery } = useDiscoveryHost();
  const publicSlug = resolveCurrentPublicDiscoverySlug();
  const [demoOpen, setDemoOpen] = useState(false);
  const demoTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!demoOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDemoOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [demoOpen]);

  return (
    <div dir="ltr" className="min-h-screen overflow-hidden bg-surface text-foreground">
      <header className="mx-auto flex max-w-7xl items-center px-6 pt-8 sm:px-8 sm:pt-10 lg:px-0">
        <Link
          to="/"
          search={isExistingManagerPublicView ? { view: "public" } : {}}
          aria-label="Qotta home"
          className="rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
        >
          <Logo size="large" className="text-brand-logo !text-[2.75rem] tracking-[-0.06em]" />
        </Link>
        {canOpenOnboarding && <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => openOnboarding()}
          className="ms-auto inline-flex h-10 items-center justify-center rounded-full border border-primary/20 bg-primary-soft px-5 text-sm font-bold text-primary transition hover:border-primary/35 hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Create my Qotta preview
        </button>}
      </header>

      <main>
        <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-16 sm:px-8 sm:pt-20 lg:grid-cols-[1.04fr_0.96fr] lg:px-0 lg:pb-28 lg:pt-24">
          <div className="animate-fade-up">
            <p className="text-xs font-extrabold tracking-[0.18em] text-primary uppercase">
              Guided discovery for project-based businesses
            </p>
            <h1 className="mt-5 max-w-[720px] text-[2.9rem] leading-[1.02] font-extrabold tracking-[-0.055em] sm:text-[4.2rem]">
              A Smarter Estimate Widget for Contractors
            </h1>
            <div className="mt-7 max-w-[680px] space-y-4 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              <p className="font-semibold text-foreground">
                Turn more of your existing website traffic into qualified sales opportunities.
              </p>
              <p>
                Qotta guides visitors through a short discovery process, helps them find the right
                solution, gives them a personalized price estimate, and sends your team a lead with
                the full project context.
              </p>
            </div>
            {canOpenOnboarding && <button
              type="button"
              aria-haspopup="dialog"
          onClick={() => openOnboarding()}
              className="mt-9 inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-primary px-7 text-base font-bold text-primary-foreground shadow-sm transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Create my Qotta preview
              <ArrowRight className="size-4" aria-hidden />
            </button>}
          </div>

          <div className="animate-slide-in relative">
            <div
              className="absolute -inset-5 rounded-[36px] bg-primary-soft/45 blur-2xl"
              aria-hidden
            />
            <div className="relative overflow-hidden rounded-[28px] border border-border bg-card p-3 shadow-[0_28px_70px_-36px_var(--shadow-color-strong)] sm:p-4">
              <img
                src="/images/qotta-product-video-poster.jpg"
                alt="A website visitor exploring a kitchen business website on a desktop computer"
                width="1280"
                height="720"
                className="aspect-video w-full rounded-[20px] object-cover"
              />
              <div className="grid gap-3 p-3 pt-5 sm:grid-cols-3 sm:p-4 sm:pt-6">
                <HeroSignal
                  icon={<MousePointerClick className="size-4" aria-hidden />}
                  label="Guided questions"
                />
                <HeroSignal
                  icon={<Sparkles className="size-4" aria-hidden />}
                  label="Right solution"
                />
                <HeroSignal
                  icon={<BriefcaseBusiness className="size-4" aria-hidden />}
                  label="Qualified lead"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-card/55">
          <div className="mx-auto grid max-w-6xl gap-10 px-6 py-20 sm:px-8 lg:grid-cols-[0.86fr_1.14fr] lg:px-0 lg:py-24">
            <SectionHeading
              eyebrow="Beyond basic estimating"
              title="Give visitors more than a quote calculator"
            />
            <div className="space-y-5 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              <p>Most estimate widgets start with measurements and end with a number.</p>
              <p>That works when the buyer already knows exactly what they need.</p>
              <div className="rounded-[22px] border border-primary/15 bg-primary-soft/60 p-6 text-foreground sm:p-7">
                <p className="font-bold">But many homeowners arrive with a different problem:</p>
                <p className="mt-2 text-muted-foreground">
                  they know what they want to accomplish, but they do not yet know the right
                  product, configuration, scope, or realistic price.
                </p>
              </div>
              <p className="font-semibold text-foreground">
                Qotta helps them figure that out before asking them to contact you.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-0 lg:py-28">
          <SectionHeading eyebrow="A short, useful path" title="How Qotta works" centered />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <article
                  key={step.title}
                  className="rounded-[24px] border border-border bg-card p-6 shadow-[0_18px_45px_-38px_var(--shadow-color-strong)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="grid size-10 place-items-center rounded-full bg-primary-soft text-primary">
                      <Icon className="size-4" aria-hidden />
                    </span>
                    <span className="text-xs font-extrabold tracking-[0.14em] text-muted-foreground uppercase">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 text-lg leading-snug font-extrabold">{step.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground">{step.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="bg-foreground text-background">
          <div className="mx-auto max-w-6xl px-6 py-20 sm:px-8 lg:px-0 lg:py-24">
            <div className="max-w-3xl">
              <p className="text-xs font-extrabold tracking-[0.18em] text-primary-soft uppercase">
                A clearer buyer journey
              </p>
              <h2 className="mt-4 text-3xl leading-tight font-extrabold tracking-[-0.035em] sm:text-4xl">
                Estimate widget vs. guided discovery
              </h2>
              <p className="mt-5 text-base leading-7 text-background/70 sm:text-lg">
                A traditional estimate widget is primarily a calculator. Qotta combines estimating
                with guided discovery.
              </p>
            </div>
            <div className="mt-10 grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
              <JourneyCard
                label="Traditional estimate widget"
                items={["Measurements", "Price", "Contact form"]}
              />
              <JourneyCard
                label="Qotta"
                items={["Need", "Discovery", "Right solution", "Estimate", "Qualified lead"]}
                highlighted
              />
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-12 px-6 py-20 sm:px-8 lg:grid-cols-[0.88fr_1.12fr] lg:px-0 lg:py-28">
          <div>
            <SectionHeading eyebrow="Where Qotta fits" title="Built for project-based businesses" />
            <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              Qotta is designed for businesses where the final price depends on the customer's
              needs, configuration, project scope, or selected solution.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {projectBusinesses.map((business) => (
              <li
                key={business}
                className="flex items-center gap-3 rounded-[18px] border border-border bg-card px-5 py-4 text-sm font-bold"
              >
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary-soft text-primary">
                  <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
                </span>
                {business}
              </li>
            ))}
          </ul>
        </section>

        <section className="border-y border-border bg-card/55">
          <div className="mx-auto max-w-6xl px-6 py-20 sm:px-8 lg:px-0 lg:py-24">
            <div className="rounded-[28px] border border-border bg-surface p-7 shadow-[0_24px_70px_-48px_var(--shadow-color-strong)] sm:p-10 lg:grid lg:grid-cols-[0.72fr_1.28fr] lg:gap-12 lg:p-12">
              <div className="grid size-14 place-items-center rounded-[18px] bg-primary-soft text-primary">
                <Globe className="size-6" aria-hidden />
              </div>
              <div className="mt-7 lg:mt-0">
                <h2 className="text-3xl leading-tight font-extrabold tracking-[-0.035em] sm:text-4xl">
                  Use the traffic you already have
                </h2>
                <div className="mt-6 space-y-4 text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
                  <p>
                    You may already be paying to bring visitors to your website through Google,
                    Meta, referrals, SEO, or other channels.
                  </p>
                  <p className="font-semibold text-foreground">
                    Qotta does not try to create more traffic.
                  </p>
                  <p>
                    It helps you get more value from the traffic that is already there by giving
                    visitors a reason to continue, understand their options, and become a
                    better-informed sales opportunity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="qotta-preview"
          className="mx-auto max-w-5xl px-6 py-20 sm:px-8 lg:px-0 lg:py-28"
        >
          <div className="rounded-[30px] bg-primary px-6 py-12 text-center text-primary-foreground shadow-[0_30px_80px_-46px_var(--shadow-color-strong)] sm:px-10 sm:py-16">
            <p className="text-xs font-extrabold tracking-[0.18em] text-primary-foreground/70 uppercase">
              Personalized to your business
            </p>
            <h2 className="mt-4 text-3xl leading-tight font-extrabold tracking-[-0.04em] sm:text-4xl">
              See Qotta on your own website
            </h2>
            {canOpenOnboarding && <><p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-primary-foreground/80 sm:text-lg">
              Explore what Qotta could do for your business with a personalized preview.
            </p>
            <button type="button" aria-haspopup="dialog" onClick={() => openOnboarding()}
              className="mt-7 inline-flex h-[54px] items-center justify-center gap-3 rounded-full bg-foreground px-7 text-base font-bold text-background transition hover:bg-foreground/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground">
              Create my Qotta preview <ArrowRight className="size-4" aria-hidden />
            </button></>}
            <p className="mt-6 text-sm text-primary-foreground/75">
              Just want to explore?{" "}
              <button
                ref={demoTriggerRef}
                type="button"
                onClick={() => setDemoOpen(true)}
                className="font-bold text-primary-foreground underline decoration-primary-foreground/45 underline-offset-4 transition hover:decoration-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground"
              >
                View demo
              </button>
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border px-6 py-8 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center">
          <span>Qotta turns existing website traffic into qualified sales opportunities.</span>
          <Link to="/" search={isExistingManagerPublicView ? { view: "public" } : {}} className="font-semibold text-foreground hover:text-primary sm:ms-auto">
            Back to Qotta home
          </Link>
        </div>
      </footer>

      {demoOpen && (
        <DemoModal
          managerPublicView={isExistingManagerPublicView}
          onClose={() => setDemoOpen(false)}
          onCustomerExperience={() => {
            demoTriggerRef.current?.focus();
            openDiscovery({ publicCustomerSlug: publicSlug, mode: "demo" });
            setDemoOpen(false);
          }}
        />
      )}
    </div>
  );
}

function HeroSignal({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-full bg-surface-elevated px-3 py-2.5 text-xs font-bold text-foreground">
      <span className="text-primary">{icon}</span>
      {label}
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  centered = false,
}: {
  eyebrow: string;
  title: string;
  centered?: boolean;
}) {
  return (
    <div className={centered ? "mx-auto max-w-3xl text-center" : "max-w-2xl"}>
      <p className="text-xs font-extrabold tracking-[0.18em] text-primary uppercase">{eyebrow}</p>
      <h2 className="mt-4 text-3xl leading-tight font-extrabold tracking-[-0.035em] sm:text-4xl">
        {title}
      </h2>
    </div>
  );
}

function JourneyCard({
  label,
  items,
  highlighted = false,
}: {
  label: string;
  items: readonly string[];
  highlighted?: boolean;
}) {
  return (
    <article
      className={`rounded-[24px] border p-6 sm:p-7 ${
        highlighted
          ? "border-primary/55 bg-primary/20"
          : "border-background/15 bg-background/[0.06]"
      }`}
    >
      <h3 className="text-sm font-extrabold tracking-[0.08em] text-background/75 uppercase">
        {label}
      </h3>
      <div
        className={`mt-6 flex flex-wrap items-center gap-2 ${
          highlighted ? "lg:flex-nowrap lg:gap-1.5" : ""
        }`}
      >
        {items.map((item, index) => (
          <div key={item} className="contents">
            <span
              className={`rounded-full px-3.5 py-2 text-sm font-bold ${
                highlighted
                  ? "bg-primary text-primary-foreground lg:px-3 lg:text-[13px] lg:whitespace-nowrap"
                  : "bg-background/10 text-background"
              }`}
            >
              {item}
            </span>
            {index < items.length - 1 && (
              <ArrowRight className="size-4 shrink-0 text-background/45" aria-hidden />
            )}
          </div>
        ))}
      </div>
    </article>
  );
}
